import { useEffect, useRef } from "preact/hooks";

import { t } from "../i18n";
import { fuel, selectedStationId } from "../state";
import type { Station } from "../types";

interface Props {
  station: Station;
  rank: number;
}

export function StationCard({ station, rank }: Props) {
  const f = fuel.value;
  const price = station.prices[f];
  const isSelected = selectedStationId.value === station.id;
  const ref = useRef<HTMLDivElement>(null);

  // When this card becomes selected (e.g. because the user tapped a
  // pin on the map), pull it into view in the list scroller. block:
  // "nearest" avoids jumping past cards that are already visible.
  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isSelected]);

  return (
    <div
      ref={ref}
      class={`station-card${isSelected ? " station-card--selected" : ""}`}
      onClick={() => {
        selectedStationId.value = station.id;
      }}
      data-station-id={station.id}
    >
      <div class="station-card__main">
        <div class="station-card__header">
          <span class="station-card__rank">{rank}.</span>
          <span class="station-card__brand">{station.brand}</span>
        </div>
        <div class="station-card__meta">
          <div class="station-card__town">{station.town}</div>
          <div class="station-card__address">{station.address}</div>
          {station.hours && (
            <div class="station-card__hours">{station.hours}</div>
          )}
        </div>
      </div>
      <div class="station-card__price">
        {price != null ? `${price.toFixed(3)} €` : "—"}
      </div>
      <a
        class="station-card__nav"
        href={`https://www.google.com/maps?q=${station.lat},${station.lng}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label="Google Maps"
      >
        <span class="station-card__nav-icon" aria-hidden="true">
          📍
        </span>
        <span class="station-card__nav-label">{t("card_navigate")}</span>
      </a>
    </div>
  );
}
